import React, { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import {
    createCard,
    validateImage,
    compressCardImage,
    generateMockDiscreteReadings,
    DEVICE_TYPES,
    SENSOR_TYPE_OPTIONS,
    getSensorTypeDefinition,
    macMatchesSensorType,
    applySensorTypeToMacAddress,
    buildMacPrefixForSensorType,
} from "../../utils/cardUtils"
import { processTimeStamp } from "../../utils/chartUtils";
import { getMqttData } from "../../utils/useDashboardHooks";
import { setMqttAddresses } from "../../utils/mqttUtils";
import Loading from "../common/Loading";
import MacAddressInput from "../common/MacAddressInput";
import PropTypes from "prop-types";
import { useAddCard } from "../../utils/reactQueryHooks";

export default function CardModal({ closeModal }) {
    const addCardMutation = useAddCard();
    
    //State to hold form input values
    const [sensorType, setSensorType] = useState(DEVICE_TYPES.MQTT);
    const [cardImage, setCardImage] = useState(null);
    const [macAddress, setMacAddress] = useState(() => buildMacPrefixForSensorType(DEVICE_TYPES.MQTT));
    const [sensorDescription, setSensorDescription] = useState("");
    const [imageUploaded, setImageUploaded] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");

    const selectedTypeDefinition = getSensorTypeDefinition(sensorType);

    // Render above header/sidebar and prevent background scroll while open.
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    const handleSensorTypeChange = (nextType) => {
        setSensorType(nextType);
        setMacAddress((current) => applySensorTypeToMacAddress(current, nextType));
    };

    const handleImageChange = async (event) => {
        const file = event.target.files[0];

        //if a file isn't selected, just return:
        if (!file) {
            return;
        }

        // Reset states
        setUploadError("");
        setIsUploading(true);
        setImageUploaded(false);
        setUploadedFileName("");

        //Always downscale/compress so we never store full-resolution photos.
        try {
            const processedFile = await compressCardImage(file);
            setImageUploaded(true);
            setUploadedFileName(file.name);

            //Read and set the image:
            const reader = new FileReader();
            reader.onloadend = () => {
                setCardImage(reader.result);
                setIsUploading(false);
            };
            reader.readAsDataURL(processedFile);
        }
        catch (ex) {
            console.error("Image processing error: ", ex);
            setUploadError('Error processing image. Please try again.');
            setIsUploading(false);
        }
    }

    const handleRemoveImage = () => {
        setCardImage(null);
        setImageUploaded(false);
        setUploadedFileName("");
        setUploadError("");
        // Reset the file input
        const fileInput = document.getElementById('imageUpload');
        if (fileInput) {
            fileInput.value = '';
        }
    }

    // Function to handle the form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!macAddress || !sensorDescription) {
            alert("Please fill in all fields.");
            return;
        }

        if (!macMatchesSensorType(macAddress, sensorType)) {
            const definition = getSensorTypeDefinition(sensorType);
            alert(`MAC address must start with "${definition.macPrefix}:" for ${definition.label}.`);
            return;
        }

        if(cardImage) {
            // Check for valid image type and whether it was compressed correctly
            const isValidImage = validateImage(cardImage);
            if (!isValidImage) {
                alert("Invalid image. Please upload a valid image file.");
                return; 
            }
        }

        const persistCard = (newCard) => {
            addCardMutation.mutate(newCard, {
                onSuccess: () => {
                    closeModal();
                },
                onError: (error) => {
                    console.error("Failed to add card:", error);
                    alert(`Error adding card: ${error.message}`);
                }
            });
        };

        // Discrete / LoRa devices report on a slow cadence and (for now) are
        // mocked client-side — no live MQTT subscription. Seed the card with a
        // recent mock reading so the dashboard tile and detail view have data.
        if (sensorType === DEVICE_TYPES.DISCRETE) {
            const readings = generateMockDiscreteReadings(macAddress, 24);
            const latest = readings[readings.length - 1] || {};
            const newCard = createCard(
                macAddress,
                sensorDescription,
                cardImage,
                latest.battery ?? 0.0,
                latest.temperature != null ? String(latest.temperature) : "",
                latest.soilMoisture != null ? String(latest.soilMoisture) : "",
                processTimeStamp(latest.timestamp) ?? ""
            );
            persistCard(newCard);
            return;
        }

        if (sensorType === DEVICE_TYPES.MQTT) {
            try {
                const sensorData = await getMqttData(macAddress);
                
                // Add MAC address to MQTT addresses for live data
                setMqttAddresses(macAddress);
    
                const mostRecentBattery = 0.0;
                const mostRecentTemp = sensorData?.temperature?.toPrecision(4) ?? "";
                const mostRecentHumidity = sensorData?.humidity?.toPrecision(4) ?? "";
                const mostRecentTimestamp = processTimeStamp(sensorData?.timestamp) ?? "";
    
                // Create the new card object
                const newCard = createCard(
                    macAddress,
                    sensorDescription,
                    cardImage,
                    mostRecentBattery,
                    mostRecentTemp,
                    mostRecentHumidity,
                    mostRecentTimestamp
                );
                
                persistCard(newCard);
            } catch (error) {
                console.error("Error fetching MQTT data:", error);
                alert("Failed to fetch sensor data. Please try again.");
            }
        }
    };

    if (addCardMutation.isLoading) return <Loading />;

    return createPortal(
        <div className="card-modal" role="dialog" aria-modal="true" aria-labelledby="card-modal-title">
            <div className="card-modal-content">
                 <h2 id="card-modal-title" className="card-modal-header">Add New Sensor</h2>
                <form onSubmit={handleSubmit}>
                    <div className="card-form-group card-sensor-type-group">
                        <span className="card-modal-label">Sensor type</span>
                        <div className="card-sensor-type-picker" role="radiogroup" aria-label="Sensor type">
                            {SENSOR_TYPE_OPTIONS.map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={sensorType === option.id}
                                    className={`card-sensor-type-option card-sensor-type-option--${option.id} ${
                                        sensorType === option.id ? 'active' : ''
                                    }`}
                                    onClick={() => handleSensorTypeChange(option.id)}
                                >
                                    <i className={option.icon} aria-hidden="true"></i>
                                    <span className="card-sensor-type-option-label">{option.shortLabel}</span>
                                    <span className="card-sensor-type-option-cadence">{option.cadence}</span>
                                </button>
                            ))}
                        </div>
                        <p className="card-sensor-type-desc">{selectedTypeDefinition.description}</p>
                    </div>

                    <div className="card-form-group">
                        <label htmlFor="Description" className="card-modal-label">Description:</label>
                        <input
                            type="text"
                            id="description"
                            value={sensorDescription}
                            maxLength="116"
                            placeholder={
                                sensorType === DEVICE_TYPES.DISCRETE
                                    ? "e.g. Front lawn zone 1"
                                    : "e.g. Greenhouse A"
                            }
                            onChange={(e) => setSensorDescription(e.target.value)}
                        />
                    </div>
                    <div className="card-form-group">
                        <label htmlFor="macAddress" className="card-modal-label">MAC Address:</label>
                        <MacAddressInput
                            id="macAddress"
                            value={macAddress}
                            onChange={(e) => setMacAddress(e.target.value)}
                            format="XX:XX:XX:XX:XX:XX:XX:XX"
                            pattern="[A-Za-z0-9]{2}(:[A-Za-z0-9]{2}){7}"
                        />
                        <p className="card-modal-hint">
                            Must start with <code>{selectedTypeDefinition.macPrefix}:</code> for {selectedTypeDefinition.shortLabel} sensors.
                        </p>
                    </div>

                    {/*Image upload*/}
                    <div className="card-form-group">
                        <label htmlFor="imageUpload" className="card-modal-label">Upload Image:</label>
                        <input
                            type="file"
                            id="imageUpload"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="file-input"
                        />
                        
                        <div className="file-upload-container">
                            <label htmlFor="imageUpload" className={`custom-file-upload ${imageUploaded ? 'uploaded' : ''} ${isUploading ? 'uploading' : ''}`}>
                                {isUploading ? (
                                    <>
                                        <i className="ri-loader-4-line upload-icon loading"></i>
                                        <span>Processing...</span>
                                    </>
                                ) : imageUploaded ? (
                                    <>
                                        <i className="ri-check-line upload-icon success"></i>
                                        <span>Change Image</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-upload-line upload-icon"></i>
                                        <span>Choose File</span>
                                    </>
                                )}
                            </label>
                            
                            {imageUploaded && (
                                <button 
                                    type="button" 
                                    className="remove-image-btn"
                                    onClick={handleRemoveImage}
                                    title="Remove image"
                                >
                                    <i className="ri-delete-bin-line"></i>
                                </button>
                            )}
                        </div>
                        
                        {uploadedFileName && (
                            <div className="file-info">
                                <i className="ri-image-line"></i>
                                <span className="file-name">{uploadedFileName}</span>
                            </div>
                        )}
                        
                        {uploadError && (
                            <div className="upload-error">
                                <i className="ri-error-warning-line"></i>
                                <span>{uploadError}</span>
                            </div>
                        )}
                    </div>

                    <div className="card-form-actions">
                        <button type="submit" className="green-button" style={{marginLeft: "16px"}}>Add Card</button>
                        <button type="button" className="normal-button delete-button" onClick={closeModal}>
                            <i className="ri-close-line custom-close-icon"></i>
                        </button>
                    </div>
                    <br/>
                </form>
            </div>
        </div>,
        document.body
    );
};

CardModal.propTypes = {
    closeModal: PropTypes.func.isRequired
}
